import React, { useEffect, useId, useRef } from 'react';
import { TIME_FILTERS } from './discoveryItems';

function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M3 4.5 6 7.5 9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function Pin() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M7 1.6c2.1 0 3.8 1.7 3.8 3.8 0 2.6-3.8 6.8-3.8 6.8S3.2 8 3.2 5.4C3.2 3.3 4.9 1.6 7 1.6Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <circle cx="7" cy="5.3" r="1.2" fill="currentColor" />
    </svg>
  );
}

function PlaceDropdown({ places, placeId, placeLabel, onSelect }) {
  const rootRef = useRef(null);
  const menuId = useId();
  const canChange = (places || []).length > 1;

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !canChange) return undefined;

    const onDoc = (event) => {
      if (!root.contains(event.target)) root.open = false;
    };
    const onKey = (event) => {
      if (event.key === 'Escape') root.open = false;
    };
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [canChange]);

  const choose = (id) => {
    if (rootRef.current) rootRef.current.open = false;
    onSelect?.(id);
  };

  if (!canChange) {
    return (
      <div className="home-disc-place-dd">
        <span className="home-disc-cluster-place">
          <Pin />
          {placeLabel}
        </span>
      </div>
    );
  }

  return (
    <details ref={rootRef} className="home-disc-place-dd">
      <summary
        className="home-disc-cluster-place"
        aria-haspopup="listbox"
        aria-controls={menuId}
        aria-label={`Browsing ${placeLabel}. Change place`}
      >
        <Pin />
        {placeLabel}
        <Chevron />
      </summary>
      <div className="home-disc-place-menu" id={menuId}>
        <div className="home-disc-place-menu-inner">
          <ul role="listbox" aria-label="Place">
            {(places || []).map((place) => (
              <li key={place.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={place.id === placeId}
                  className={place.id === placeId ? 'is-on' : ''}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    choose(place.id);
                  }}
                >
                  {place.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </details>
  );
}

function TimeTabs({ time, onTime, filters }) {
  const chips = filters?.length ? filters : TIME_FILTERS;
  return (
    <div className="home-disc-cluster-time" role="tablist" aria-label="When">
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          role="tab"
          aria-selected={time === chip.id}
          className={time === chip.id ? 'is-on' : ''}
          onClick={() => onTime(chip.id)}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

function CategoryChips({ categories, categoryId, onSelect }) {
  if (!categories?.length) return null;
  return (
    <div className="home-disc-cats" role="tablist" aria-label="Category">
      {categories.map((chip) => {
        const on = categoryId === chip.id;
        return (
          <button
            key={chip.id}
            type="button"
            role="tab"
            aria-selected={on}
            className={on ? 'is-on' : ''}
            onClick={() => onSelect(on ? null : chip.id)}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

export default function DiscoveryChrome({
  time,
  onTime,
  timeFilters,
  places,
  placeId,
  placeLabel,
  onSelectPlace,
  categories,
  categoryId,
  onSelectCategory,
  onSearch,
  shortcuts,
}) {
  return (
    <>
      <header className="home-disc-head">
        <h2>What&apos;s on</h2>
      </header>
      <div className="home-disc-cluster">
        <PlaceDropdown
          places={places}
          placeId={placeId}
          placeLabel={placeLabel}
          onSelect={onSelectPlace}
        />
        <TimeTabs time={time} onTime={onTime} filters={timeFilters} />
      </div>
      <CategoryChips
        categories={categories}
        categoryId={categoryId}
        onSelect={onSelectCategory}
      />
      {shortcuts?.length ? (
        <>
          <p className="home-disc-jump">Trending searches</p>
          <div className="home-disc-links" aria-label="Trending searches">
            {shortcuts.map((query, index) => (
              <React.Fragment key={query}>
                {index > 0 ? <span className="home-disc-links-sep">/</span> : null}
                <button type="button" onClick={() => onSearch(query)}>
                  {query}
                </button>
              </React.Fragment>
            ))}
          </div>
        </>
      ) : null}
    </>
  );
}
